import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Plus, ClipboardList, Pencil, Trash2, X, Share2, EyeOff } from "lucide-react";

const empty = { title: "", description: "", status: "draft", share_results: true, portal_link: "", questions: [] };
const uid = () => Math.random().toString(36).slice(2);
const typeLabels = { single: "Single choice", multiple: "Multiple choice", scale: "Rating scale", text: "Free text" };

export default function Quizzes() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [toDelete, setToDelete] = useState(null);

  const { data: quizzes = [], isLoading } = useQuery({
    queryKey: ["quizzes"],
    queryFn: async () => (await api.get("/quizzes")).data,
  });

  const save = useMutation({
    mutationFn: async (payload) =>
      editing ? (await api.put(`/quizzes/${editing}`, payload)).data : (await api.post("/quizzes", payload)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["quizzes"] });
      qc.invalidateQueries({ queryKey: ["dashboard-stats"] });
      toast.success(editing ? "Quiz updated" : "Quiz created");
      setOpen(false);
    },
    onError: () => toast.error("Could not save quiz"),
  });

  const del = useMutation({
    mutationFn: async (id) => (await api.delete(`/quizzes/${id}`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["quizzes"] });
      qc.invalidateQueries({ queryKey: ["dashboard-stats"] });
      toast.success("Quiz deleted");
      setToDelete(null);
    },
  });

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (q) => { setEditing(q.id); setForm({ ...empty, ...q, questions: q.questions || [] }); setOpen(true); };

  const addQuestion = () => setForm((f) => ({ ...f, questions: [...f.questions, { id: uid(), text: "", type: "single", options: [] }] }));
  const updateQuestion = (qi, key, val) => setForm((f) => { const q = [...f.questions]; q[qi] = { ...q[qi], [key]: val }; return { ...f, questions: q }; });
  const removeQuestion = (qi) => setForm((f) => ({ ...f, questions: f.questions.filter((_, i) => i !== qi) }));
  const addOption = (qi) => setForm((f) => { const q = [...f.questions]; q[qi] = { ...q[qi], options: [...q[qi].options, { id: uid(), text: "" }] }; return { ...f, questions: q }; });
  const updateOption = (qi, oi, val) => setForm((f) => { const q = [...f.questions]; const o = [...q[qi].options]; o[oi] = { ...o[oi], text: val }; q[qi] = { ...q[qi], options: o }; return { ...f, questions: q }; });
  const removeOption = (qi, oi) => setForm((f) => { const q = [...f.questions]; q[qi] = { ...q[qi], options: q[qi].options.filter((_, i) => i !== oi) }; return { ...f, questions: q }; });

  return (
    <div data-testid="quizzes-page">
      <PageHeader
        title="Quizzes & Surveys"
        subtitle="Create wellbeing check-ins and self-reflection surveys. Client results stay private to each client."
        action={
          <Button data-testid="new-quiz-button" onClick={openNew} className="rounded-full bg-gold-500 px-6 text-white hover:bg-gold-600 shadow-ambient">
            <Plus className="mr-2 h-4 w-4" /> New Quiz
          </Button>
        }
      />

      {isLoading ? (
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">{[1, 2, 3].map((i) => <div key={i} className="h-52 animate-pulse rounded-2xl bg-white/60" />)}</div>
      ) : quizzes.length === 0 ? (
        <Card className="flex flex-col items-center justify-center rounded-2xl border-dashed border-navy-100 bg-white/60 py-20 text-center shadow-none">
          <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-gold-50"><ClipboardList className="h-8 w-8 text-gold-500" /></div>
          <h3 className="mt-5 font-serif text-xl font-bold text-navy-900">No quizzes yet</h3>
          <p className="mt-2 max-w-sm text-navy-500">Build a wellbeing check-in to help clients reflect on their journey.</p>
          <Button onClick={openNew} className="mt-6 rounded-full bg-gold-500 text-white hover:bg-gold-600"><Plus className="mr-2 h-4 w-4" /> New Quiz</Button>
        </Card>
      ) : (
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {quizzes.map((q) => (
            <Card key={q.id} data-testid={`quiz-card-${q.id}`} className="rounded-2xl border-navy-900/5 bg-white p-6 shadow-ambient transition-all duration-300 hover:-translate-y-1 hover:shadow-ambient-lg">
              <div className="flex items-start justify-between">
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-rose-50"><ClipboardList className="h-5 w-5 text-rose-500" /></div>
                <Badge className={`border-0 ${q.status === "published" ? "bg-emerald-500 text-white" : "bg-navy-50 text-navy-600"}`}>{q.status}</Badge>
              </div>
              <h3 className="mt-4 font-serif text-lg font-bold text-navy-900">{q.title}</h3>
              <p className="mt-1 line-clamp-2 text-sm text-navy-500">{q.description || "No description."}</p>
              <div className="mt-3 flex items-center gap-3 text-xs text-navy-400">
                <span>{q.questions?.length || 0} questions</span>
                <span className="flex items-center gap-1">
                  {q.share_results ? <><Share2 className="h-3.5 w-3.5" /> Results shared</> : <><EyeOff className="h-3.5 w-3.5" /> Private</>}
                </span>
              </div>
              <div className="mt-4 flex gap-2">
                <Button data-testid={`edit-quiz-${q.id}`} variant="outline" size="sm" onClick={() => openEdit(q)} className="flex-1 rounded-full border-navy-100"><Pencil className="mr-1.5 h-3.5 w-3.5" /> Edit</Button>
                <Button data-testid={`delete-quiz-${q.id}`} variant="outline" size="sm" onClick={() => setToDelete(q)} className="rounded-full border-navy-100 text-destructive hover:bg-destructive/10"><Trash2 className="h-3.5 w-3.5" /></Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto bg-white">
          <DialogHeader><DialogTitle className="font-serif text-2xl text-navy-900">{editing ? "Edit Quiz" : "New Quiz"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Title</Label><Input data-testid="quiz-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1 focus-visible:ring-gold-500" placeholder="Wellbeing Check-In" /></div>
            <div><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1 focus-visible:ring-gold-500" rows={2} /></div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger className="mt-1" data-testid="quiz-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white"><SelectItem value="draft">Draft</SelectItem><SelectItem value="published">Published</SelectItem></SelectContent>
                </Select>
              </div>
              <div className="flex items-end pb-2">
                <div className="flex items-center gap-3 rounded-lg bg-ivory px-4 py-2">
                  <Switch checked={form.share_results} onCheckedChange={(v) => setForm({ ...form, share_results: v })} data-testid="quiz-share-switch" />
                  <span className="text-sm text-navy-700">Share results with client</span>
                </div>
              </div>
            </div>

            <div>
              <Label>GHL survey link (optional)</Label>
              <Input data-testid="quiz-portal-link-input" value={form.portal_link} onChange={(e) => setForm({ ...form, portal_link: e.target.value })} className="mt-1 font-mono text-sm focus-visible:ring-gold-500" placeholder="https://…  (the link clients open to take this survey)" />
              <p className="mt-1 text-xs text-navy-400">Paste the shareable GHL survey/form link. Clients tap "Start check-in" to open it.</p>
            </div>

            <div className="rounded-xl bg-ivory p-4">
              <div className="mb-3 flex items-center justify-between">
                <Label className="text-navy-800">Questions</Label>
                <Button type="button" size="sm" variant="outline" onClick={addQuestion} data-testid="add-question-button" className="rounded-full border-navy-100"><Plus className="mr-1 h-3.5 w-3.5" /> Question</Button>
              </div>
              <div className="space-y-3">
                {form.questions.map((q, qi) => (
                  <div key={q.id} className="rounded-lg border border-navy-100 bg-white p-3">
                    <div className="flex items-center gap-2">
                      <Input value={q.text} onChange={(e) => updateQuestion(qi, "text", e.target.value)} placeholder={`Question ${qi + 1}`} className="focus-visible:ring-gold-500" />
                      <Select value={q.type} onValueChange={(v) => updateQuestion(qi, "type", v)}>
                        <SelectTrigger className="w-40 shrink-0"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-white">{Object.entries(typeLabels).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                      </Select>
                      <Button type="button" size="icon" variant="ghost" onClick={() => removeQuestion(qi)} className="text-destructive"><X className="h-4 w-4" /></Button>
                    </div>
                    {(q.type === "single" || q.type === "multiple") && (
                      <div className="mt-2 space-y-2 pl-3">
                        {q.options.map((o, oi) => (
                          <div key={o.id} className="flex items-center gap-2">
                            <Input value={o.text} onChange={(e) => updateOption(qi, oi, e.target.value)} placeholder={`Option ${oi + 1}`} className="h-9 text-sm focus-visible:ring-gold-500" />
                            <Button type="button" size="icon" variant="ghost" onClick={() => removeOption(qi, oi)} className="h-8 w-8 text-navy-300"><X className="h-3.5 w-3.5" /></Button>
                          </div>
                        ))}
                        <Button type="button" size="sm" variant="ghost" onClick={() => addOption(qi)} className="text-gold-600 hover:text-gold-700"><Plus className="mr-1 h-3.5 w-3.5" /> Add option</Button>
                      </div>
                    )}
                  </div>
                ))}
                {form.questions.length === 0 && <p className="text-sm text-navy-400">No questions yet.</p>}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-full">Cancel</Button>
            <Button data-testid="save-quiz-button" disabled={!form.title || save.isPending} onClick={() => save.mutate(form)} className="rounded-full bg-gold-500 text-white hover:bg-gold-600">{save.isPending ? "Saving..." : "Save Quiz"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader><AlertDialogTitle>Delete quiz?</AlertDialogTitle><AlertDialogDescription>"{toDelete?.title}" will be permanently removed.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel className="rounded-full">Cancel</AlertDialogCancel><AlertDialogAction data-testid="confirm-delete-quiz" onClick={() => del.mutate(toDelete.id)} className="rounded-full bg-destructive hover:bg-destructive/90">Delete</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
